import { WrappedData, TravelPersonalityType } from './types/wrapped'

// ============================================================================
// MOCK DATA FACTORY
// ============================================================================

/**
 * Creates a base WrappedData object with sensible defaults
 */
export function createMockWrappedData(overrides: Partial<WrappedData> = {}): WrappedData {
  return {
    personality: {
      type: 'the-sun-chaser',
      percentile: 12,
    },
    stats: {
      totalNights: 21,
      totalDistanceKm: 15000,
      totalSavings: 3500,
      currency: 'AUD',
      citiesVisited: [
        { name: 'Bali', country: 'Indonesia', countryCode: 'ID', imageId: 'vg4uvt2y5dgkivujuba6' },
        { name: 'Tokyo', country: 'Japan', countryCode: 'JP', imageId: 'ri8b3umlvln1k6290g7i' },
      ],
      countriesVisited: 2,
      offersViewed: 150,
      averageOffersViewed: 85,
      destinationsSearched: 3,
    },
    topDestinations: [
      { id: '1', name: 'Bali', viewCount: 45, imageId: 'vg4uvt2y5dgkivujuba6' },
      { id: '2', name: 'Maldives', viewCount: 32, imageId: 'dapbr82l65bcj5iy6v8' },
      { id: '3', name: 'Paris', viewCount: 28, imageId: 'dcou83bxqnsc1p49gii' },
    ],
    topOffer: {
      id: '006RF00000P0m1UYAR',
      name: 'Luxe Beach Resort with All-Inclusive Package',
      propertyName: 'Luxe Beach Resort',
      location: 'Bali, Indonesia',
      viewCount: 12,
      imageId: 'beb8i0fyuhhtm7a8ika',
      isAlternative: false,
      inclusions: [
        { description: 'Daily breakfast', id: 'inc-1' },
        { description: 'Spa credit', id: 'inc-2' },
        { description: 'Airport transfers', id: 'inc-3' },
      ],
    },
    generatedAt: new Date().toISOString(),
    ...overrides,
  }
}

// ============================================================================
// NAMED MOCK SCENARIOS
// ============================================================================

export const MOCK_SCENARIOS = {
  /**
   * Use live data from API (no mocking)
   */
  LIVE_DATA: null as unknown as WrappedData,

  /**
   * Full traveller with all data populated
   */
  FULL_TRAVELLER: createMockWrappedData({
    loyalty: {
      tier: 'gold',
      points: 1200,
      statusCredits: 100,
      percentile: 95,
      tierProgression: {
        type: 'unlock',
        name: 'Platinum',
        nightsNeeded: 8,
        creditsNeeded: 100,
        benefits: [
          'Free room upgrades',
          'Airport lounge access',
          'Priority booking',
        ],
      },
    },
  }),

  /**
   * User with offer views but no trips booked (low data mode)
   */
  LOW_DATA_USER: createMockWrappedData({
    stats: {
      totalNights: 0,
      totalDistanceKm: 0,
      totalSavings: 0,
      currency: 'AUD',
      citiesVisited: [],
      countriesVisited: 0,
      offersViewed: 150,
      averageOffersViewed: 85,
      destinationsSearched: 3,
    },
    topDestinations: [],
  }),

  /**
   * User who visited only one city
   */
  SINGLE_CITY_TRAVELLER: createMockWrappedData({
    stats: {
      totalNights: 5,
      totalDistanceKm: 500,
      totalSavings: 800,
      currency: 'AUD',
      citiesVisited: [
        { name: 'Bali', country: 'Indonesia', countryCode: 'ID', imageId: 'vg4uvt2y5dgkivujuba6' },
      ],
      countriesVisited: 1,
      offersViewed: 50,
      averageOffersViewed: 85,
      destinationsSearched: 1,
    },
  }),

  /**
   * User who visited multiple cities in multiple countries
   */
  MULTI_COUNTRY_TRAVELLER: createMockWrappedData({
    stats: {
      totalNights: 30,
      totalDistanceKm: 25000,
      totalSavings: 5000,
      currency: 'AUD',
      citiesVisited: [
        { name: 'Paris', country: 'France', countryCode: 'FR', imageId: 'dcou83bxqnsc1p49gii' },
        { name: 'Rome', country: 'Italy', countryCode: 'IT', imageId: 't6i4r197oc9wxnwnd1oi' },
        { name: 'Barcelona', country: 'Spain', countryCode: 'ES', imageId: 'uc71bgan2n25wrcdztdr' },
        { name: 'Amsterdam', country: 'Netherlands', countryCode: 'NL', imageId: 'c3jykwi63b2qnwpqkh8j' },
      ],
      countriesVisited: 4,
      offersViewed: 200,
      averageOffersViewed: 85,
      destinationsSearched: 4,
    },
  }),

  /**
   * User with short distance travelled (< 200km)
   */
  SHORT_DISTANCE_TRAVELLER: createMockWrappedData({
    stats: {
      totalNights: 3,
      totalDistanceKm: 150,
      totalSavings: 200,
      currency: 'AUD',
      citiesVisited: [
        { name: 'Byron Bay', country: 'Australia', countryCode: 'AU', imageId: '72ahezjf2mjt0rcyi4jb' },
      ],
      countriesVisited: 1,
      offersViewed: 30,
      averageOffersViewed: 85,
      destinationsSearched: 3,
    },
  }),

  /**
   * User without loyalty data
   */
  NO_LOYALTY_USER: createMockWrappedData({
    loyalty: undefined,
  }),

  /**
   * User with bronze tier loyalty
   */
  BRONZE_LOYALTY_USER: createMockWrappedData({
    loyalty: {
      tier: 'bronze',
      points: 200,
      statusCredits: 10,
      percentile: 50,
      tierProgression: {
        type: 'unlock',
        name: 'Silver',
        nightsNeeded: 5,
        creditsNeeded: 40,
        benefits: [],
      },
    },
  }),

  /**
   * User with silver tier loyalty
   */
  SILVER_LOYALTY_USER: createMockWrappedData({
    loyalty: {
      tier: 'silver',
      points: 500,
      statusCredits: 30,
      percentile: 70,
      tierProgression: {
        type: 'unlock',
        name: 'Gold',
        nightsNeeded: 8,
        creditsNeeded: 70,
        benefits: [],
      },
    },
  }),

  /**
   * User with gold tier loyalty
   */
  GOLD_LOYALTY_USER: createMockWrappedData({
    loyalty: {
      tier: 'gold',
      points: 1200,
      statusCredits: 100,
      percentile: 90,
      tierProgression: {
        type: 'unlock',
        name: 'Platinum',
        nightsNeeded: 10,
        creditsNeeded: 150,
        benefits: [],
      },
    },
  }),

  /**
   * User with platinum tier loyalty
   */
  PLATINUM_LOYALTY_USER: createMockWrappedData({
    loyalty: {
      tier: 'platinum',
      points: 5000,
      statusCredits: 500,
      percentile: 99,
      tierProgression: {
        type: 'retain',
        name: 'Platinum',
        nightsNeeded: 5,
        creditsNeeded: 100,
        benefits: [],
      },
    },
  }),

  /**
   * User with an active/upcoming booking
   */
  ACTIVE_BOOKING_USER: createMockWrappedData({
    activeBooking: {
      orderId: 'order-123',
      daysToGo: 14,
      destination: 'Maldives',
      checkInDate: '2026-02-01',
      imageId: 'wq1j5ijfesfmg4rxsrdq',
    },
  }),

  /**
   * User with a prediction but no active booking
   */
  PREDICTION_USER: createMockWrappedData({
    prediction: {
      destinationId: 'le_2723d092b63885e0d7c260cc007e8b9d',
      destinationName: 'Maldives',
      offerCount: 45,
      maxSavingsPercent: 35,
      imageId: 'wq1j5ijfesfmg4rxsrdq',
      searchUrl: '/search?destinationName=Maldives&destinationId=le_2723d092b63885e0d7c260cc007e8b9d&adults=2&children=none',
    },
  }),

  /**
   * User with low savings (< $100)
   */
  LOW_SAVINGS_USER: createMockWrappedData({
    stats: {
      totalNights: 5,
      totalDistanceKm: 500,
      totalSavings: 50,
      currency: 'AUD',
      citiesVisited: [
        { name: 'Bali', country: 'Indonesia', countryCode: 'ID', imageId: 'vg4uvt2y5dgkivujuba6' },
      ],
      countriesVisited: 1,
      offersViewed: 30,
      averageOffersViewed: 85,
      destinationsSearched: 1,
    },
  }),

  /**
   * User with alternative offer (not their top viewed)
   */
  ALTERNATIVE_OFFER_USER: createMockWrappedData({
    topOffer: {
      id: '006RF00000OjiFCYAZ',
      name: 'Recommended Resort with All-Inclusive Package',
      propertyName: 'Recommended Resort',
      location: 'Maldives',
      viewCount: 5,
      imageId: 'wq1j5ijfesfmg4rxsrdq',
      isAlternative: true,
      inclusions: [
        { description: 'All-inclusive meals', id: 'inc-1' },
      ],
    },
  }),

  /**
   * User with multiple cities in single country
   */
  SINGLE_COUNTRY_MULTI_CITY: createMockWrappedData({
    stats: {
      totalNights: 14,
      totalDistanceKm: 2000,
      totalSavings: 1500,
      currency: 'AUD',
      citiesVisited: [
        { name: 'Sydney', country: 'Australia', countryCode: 'AU', imageId: 'hotel-destination-sydney' },
        { name: 'Melbourne', country: 'Australia', countryCode: 'AU', imageId: 'asgz0fkt6cf311m98rq' },
        { name: 'Brisbane', country: 'Australia', countryCode: 'AU', imageId: 'oyv6xfnurgx3g34hn9y' },
      ],
      countriesVisited: 1,
      offersViewed: 80,
      averageOffersViewed: 85,
      destinationsSearched: 12,
    },
  }),
} as const

export type MockScenarioKey = keyof typeof MOCK_SCENARIOS

// ============================================================================
// PERSONALITY TEST DATA
// ============================================================================

export const ALL_PERSONALITY_TYPES: Array<TravelPersonalityType> = [
  'the-bond-builder',
  'the-curious-explorer',
  'the-elite-traveller',
  'the-nature-roamer',
  'the-romance-storyteller',
  'the-sun-chaser',
  'the-wellness-wanderer',
  'the-trend-explorer',
  'the-gourmet-hunter',
]

export function createPersonalityData(type: TravelPersonalityType): WrappedData {
  return createMockWrappedData({
    personality: { type, percentile: 30 },
  })
}

// ============================================================================
// WINDOW INJECTABLE MOCK DATA (for development/QA)
// ============================================================================

declare global {
  interface Window {
    __WRAPPED_MOCK_SCENARIO__?: MockScenarioKey
    __WRAPPED_MOCK_DATA__?: Partial<WrappedData>
    // Helper functions exposed for console access
    __setWrappedScenario__?: (scenario: MockScenarioKey) => void
    __clearWrappedMock__?: () => void
  }
}

const MOCK_SCENARIO_STORAGE_KEY = '__WRAPPED_MOCK_SCENARIO__'
const MOCK_DATA_STORAGE_KEY = '__WRAPPED_MOCK_DATA__'

/**
 * Gets mock data from window or sessionStorage for development testing
 * Uses sessionStorage as fallback to persist across page navigations
 * Defaults to FULL_TRAVELLER if no scenario is set
 * Use LIVE_DATA scenario to fetch from the API instead
 */
export function getWindowMockData(): WrappedData | undefined {
  if (typeof window === 'undefined') return undefined

  // Check for scenario name first (window takes precedence over sessionStorage)
  const scenarioKey = window.__WRAPPED_MOCK_SCENARIO__ ??
    (sessionStorage.getItem(MOCK_SCENARIO_STORAGE_KEY) as MockScenarioKey | null)

  if (scenarioKey) {
    // LIVE_DATA means use real API data
    if (scenarioKey === 'LIVE_DATA') return undefined
    const scenario = MOCK_SCENARIOS[scenarioKey]
    if (scenario) return scenario
  }

  // Check for direct data override
  if (window.__WRAPPED_MOCK_DATA__) {
    return createMockWrappedData(window.__WRAPPED_MOCK_DATA__)
  }

  // Check sessionStorage for custom data
  const storedData = sessionStorage.getItem(MOCK_DATA_STORAGE_KEY)
  if (storedData) {
    try {
      return createMockWrappedData(JSON.parse(storedData))
    } catch {
      // Invalid JSON, ignore
    }
  }
}

/**
 * Sets mock scenario on window for development testing
 * Also persists to sessionStorage to survive page navigations
 */
export function setWindowMockScenario(scenario: MockScenarioKey): void {
  if (typeof window !== 'undefined') {
    window.__WRAPPED_MOCK_SCENARIO__ = scenario
    sessionStorage.setItem(MOCK_SCENARIO_STORAGE_KEY, scenario)
    // Clear custom data when setting scenario
    delete window.__WRAPPED_MOCK_DATA__
    sessionStorage.removeItem(MOCK_DATA_STORAGE_KEY)
  }
}

/**
 * Sets custom mock data on window for development testing
 * Also persists to sessionStorage to survive page navigations
 */
export function setWindowMockData(data: Partial<WrappedData>): void {
  if (typeof window !== 'undefined') {
    window.__WRAPPED_MOCK_DATA__ = data
    sessionStorage.setItem(MOCK_DATA_STORAGE_KEY, JSON.stringify(data))
    // Clear scenario when setting custom data
    delete window.__WRAPPED_MOCK_SCENARIO__
    sessionStorage.removeItem(MOCK_SCENARIO_STORAGE_KEY)
  }
}

/**
 * Clears all window mock data (both window and sessionStorage)
 */
export function clearWindowMockData(): void {
  if (typeof window !== 'undefined') {
    delete window.__WRAPPED_MOCK_SCENARIO__
    delete window.__WRAPPED_MOCK_DATA__
    sessionStorage.removeItem(MOCK_SCENARIO_STORAGE_KEY)
    sessionStorage.removeItem(MOCK_DATA_STORAGE_KEY)
  }
}

// Expose helper functions on window for easy console access
if (typeof window !== 'undefined') {
  window.__setWrappedScenario__ = setWindowMockScenario
  window.__clearWrappedMock__ = clearWindowMockData
}
